import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X, Gift, TrendingUp, CheckCircle } from 'lucide-react';

const LEAD_POPUP_KEY = 'td_lead_popup_shown';
const COOKIE_CONSENT_KEY = 'td_capital_cookie_consent';
const SIGNUP_URL = 'https://my.tdfxcapital.net/portal';

export const LeadCapturePopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Skip if we've already shown this session.
    const wasShown = sessionStorage.getItem(LEAD_POPUP_KEY);
    if (wasShown) return;

    const tryShow = () => {
      // Defer while the cookie-consent banner is still awaiting user choice —
      // avoid stacking two dialogs at once.
      if (!localStorage.getItem(COOKIE_CONSENT_KEY)) return;
      setIsVisible(true);
      sessionStorage.setItem(LEAD_POPUP_KEY, 'true');
    };

    // Show popup after 30 seconds (only if cookie consent already made)
    const timer = setTimeout(tryShow, 30000);

    // Exit intent detection (mouse leaves viewport at top)
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !sessionStorage.getItem(LEAD_POPUP_KEY)) {
        tryShow();
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    
    // Track lead capture event
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': 'lead_capture',
        'lead_source': 'exit_intent_popup',
        'email_provided': true
      });
    }

    // Simulate API call (replace with actual endpoint)
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setIsSubmitted(true);
    setIsLoading(false);

    // Redirect to signup after 2 seconds
    setTimeout(() => {
      window.open(`${SIGNUP_URL}?email=${encodeURIComponent(email)}&utm_source=popup&utm_medium=exit_intent`, '_blank');
    }, 2000);
  };

  const handleClose = () => {
    setIsVisible(false);
    // Track popup close
    if (window.dataLayer) {
      window.dataLayer.push({
        'event': 'popup_closed',
        'popup_type': 'lead_capture'
      });
    }
  };

  // ESC dismisses the popup (accessibility + easy exit).
  useEffect(() => {
    if (!isVisible) return;
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9997] transition-opacity duration-300"
        onClick={handleClose}
      />

      {/* Popup */}
      <div 
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9998] w-full max-w-lg mx-4 animate-in zoom-in-95 duration-300"
        data-testid="lead-capture-popup"
      >
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 via-purple-500 to-blue-500 p-6 text-white relative">
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
              data-testid="lead-popup-close"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-3 mb-2">
              <Gift className="h-8 w-8" />
              <span className="text-sm font-medium uppercase tracking-wider">Exclusive Offer</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold">
              Start Trading with a Welcome Bonus!
            </h2>
          </div>

          {/* Content */}
          <div className="p-6">
            {!isSubmitted ? (
              <>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">Spreads from 0.0 pips</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">Leverage up to 1:500</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">24/7 customer support</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">Free demo account with $100,000</span>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <Input
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12 text-base border-gray-300"
                    data-testid="lead-email-input"
                  />
                  <Button 
                    type="submit" 
                    className="w-full h-12 btn-premium text-white font-semibold text-base"
                    disabled={isLoading}
                    data-testid="lead-submit-btn"
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                        Processing...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <TrendingUp className="h-5 w-5" />
                        Claim Your Bonus
                      </span>
                    )}
                  </Button>
                </form>

                <p className="text-xs text-gray-500 text-center mt-4">
                  By signing up, you agree to our Terms & Conditions and Privacy Policy.
                  Trading involves risk.
                </p>
              </>
            ) : (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Thank You!</h3>
                <p className="text-gray-600 mb-4">
                  Redirecting you to complete your registration...
                </p>
                <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin mx-auto"></div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default LeadCapturePopup;
